import { useState, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useMediaQuery, MOBILE_QUERY } from '../../hooks/useMediaQuery';
import { useToast } from '../common/Toast';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Field } from '../common/Field';
import { Input, Select } from '../common/Input';
import { Modal } from '../common/Modal';
import { Sheet } from '../common/Sheet';
import { apiError } from '../../utils/apiError';
import { BulkAction, BulkInput, Group } from '../../types';

interface Props {
  selected: number[];
  total: number;
  groups: Group[];
  onSelectAll: () => void;
  onClear: () => void;
  onDone: () => void;
}

type Dialog = 'tags' | 'group' | 'interval' | 'delete' | null;

const INTERVALS = [30, 60, 120, 300, 600, 900, 1800, 3600];

/**
 * Bar for a multi-selection on the dashboard. One request per action; the
 * server does it in one transaction and one audit entry.
 *
 * Two shapes. On a wide screen it floats in the content column with every
 * action inline. On a phone it becomes the bottom bar — the tab bar stands
 * down for the duration (see `useClaimBottomBar`) rather than the two
 * stacking, which is what put this toolbar's lower rows behind the tab bar.
 * Only the two actions worth a thumb stay on the bar; the rest move to a
 * sheet, because eight wrapped buttons on a 375px screen is not a toolbar.
 */
export function BulkActionBar({ selected, total, groups, onSelectAll, onClear, onDone }: Props) {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const qc = useQueryClient();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tags, setTags] = useState('');
  const [tagMode, setTagMode] = useState<'add_tags' | 'remove_tags'>('add_tags');
  const [groupId, setGroupId] = useState('');
  const [groupMode, setGroupMode] = useState<'add_to_group' | 'remove_from_group'>('add_to_group');
  const [interval, setInterval_] = useState('300');

  const mutation = useMutation({
    mutationFn: (data: BulkInput) => api.bulkServices(data),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['service'] });
      qc.invalidateQueries({ queryKey: ['groups'] });
      const failed = result.results.filter(r => !r.ok).length;
      addToast(failed ? 'info' : 'success', `${VERB[result.action]} ${result.applied} service${result.applied === 1 ? '' : 's'}${failed ? ` (${failed} skipped)` : ''}.`);
      setDialog(null);
      onDone();
    },
    onError: (err) => addToast('error', apiError(err, 'Bulk action failed')),
  });

  const run = (action: BulkAction, extra: Partial<BulkInput> = {}) => mutation.mutate({ ids: selected, action, ...extra });
  const n = selected.length;
  const canDelete = hasPermission('services.delete');
  const busy = !n || mutation.isPending;

  /** Leaving the sheet for a dialog: one surface at a time. */
  const openDialog = (d: Dialog) => { setSheetOpen(false); setDialog(d); };

  function submitTags(e: FormEvent) {
    e.preventDefault();
    const list = tags.split(',').map(t => t.trim()).filter(Boolean);
    if (list.length) run(tagMode, { tags: list });
  }

  function submitGroup(e: FormEvent) {
    e.preventDefault();
    if (groupId) run(groupMode, { group_id: parseInt(groupId, 10) });
  }

  function submitInterval(e: FormEvent) {
    e.preventDefault();
    run('set_interval', { interval: parseInt(interval, 10) });
  }

  return (
    <>
      {isMobile ? (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-1.5 px-2 min-h-[3.25rem] pb-[env(safe-area-inset-bottom)] shadow-overlay bg-gray-900 text-gray-100 dark:bg-gray-100 dark:text-gray-900"
        >
          <button
            type="button"
            onClick={onDone}
            aria-label="Done"
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-md hover:bg-white/10 dark:hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-xs font-medium tabular-nums shrink-0">{n} of {total}</span>
          <div className="flex items-center gap-1 ml-auto">
            <BarButton onClick={() => run('pause')} disabled={busy} className="min-h-[2.75rem] px-3">Pause</BarButton>
            <BarButton onClick={() => run('resume')} disabled={busy} className="min-h-[2.75rem] px-3">Resume</BarButton>
            <BarButton onClick={() => setSheetOpen(true)} haspopup className="min-h-[2.75rem] px-3">More…</BarButton>
          </div>
        </div>
      ) : (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className="sticky bottom-4 z-20 mx-auto max-w-4xl flex flex-wrap items-center gap-2 px-4 py-3 rounded-lg shadow-overlay bg-gray-900 text-gray-100 dark:bg-gray-100 dark:text-gray-900"
        >
          <span className="text-sm font-medium tabular-nums mr-1">
            {n} of {total} selected
          </span>
          <button type="button" className="text-xs underline hover:no-underline mr-2" onClick={n === total ? onClear : onSelectAll}>
            {n === total ? 'Clear' : 'Select all shown'}
          </button>
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            <BarButton onClick={() => run('pause')} disabled={busy}>Pause</BarButton>
            <BarButton onClick={() => run('resume')} disabled={busy}>Resume</BarButton>
            <BarButton onClick={() => run('check')} disabled={busy}>Check now</BarButton>
            <BarButton onClick={() => setDialog('tags')} disabled={!n}>Tags…</BarButton>
            <BarButton onClick={() => setDialog('group')} disabled={!n || groups.length === 0}>Group…</BarButton>
            <BarButton onClick={() => setDialog('interval')} disabled={!n}>Interval…</BarButton>
            {canDelete && <BarButton onClick={() => setDialog('delete')} disabled={!n} danger>Delete</BarButton>}
            <BarButton onClick={onDone}>Done</BarButton>
          </div>
        </div>
      )}

      {/* Mobile only, or its entries would duplicate the desktop bar's. */}
      {isMobile && (
        <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={`${n} of ${total} selected`}>
          <ul className="py-1">
            <SheetAction onClick={() => { setSheetOpen(false); (n === total ? onClear : onSelectAll)(); }}>
              {n === total ? 'Clear selection' : 'Select all shown'}
            </SheetAction>
            <SheetAction onClick={() => { setSheetOpen(false); run('check'); }} disabled={busy}>Check now</SheetAction>
            <SheetAction onClick={() => openDialog('tags')} disabled={!n}>Tags…</SheetAction>
            <SheetAction onClick={() => openDialog('group')} disabled={!n || groups.length === 0}>Group…</SheetAction>
            <SheetAction onClick={() => openDialog('interval')} disabled={!n}>Interval…</SheetAction>
            {canDelete && <SheetAction onClick={() => openDialog('delete')} disabled={!n} danger>Delete</SheetAction>}
          </ul>
        </Sheet>
      )}

      <Modal open={dialog === 'tags'} onClose={() => setDialog(null)} title={`Tags for ${n} service${n === 1 ? '' : 's'}`}>
        <form onSubmit={submitTags} className="space-y-4">
          <Field label="Action">
            {p => (
              <Select {...p} value={tagMode} onChange={e => setTagMode(e.target.value as typeof tagMode)}>
                <option value="add_tags">Add tags</option>
                <option value="remove_tags">Remove tags</option>
              </Select>
            )}
          </Field>
          <Field label="Tags" required hint="Comma-separated.">
            {p => <Input {...p} required value={tags} onChange={e => setTags(e.target.value)} placeholder="prod, eu-west" autoFocus />}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" loading={mutation.isPending}>Apply</Button>
          </div>
        </form>
      </Modal>

      <Modal open={dialog === 'group'} onClose={() => setDialog(null)} title={`Group for ${n} service${n === 1 ? '' : 's'}`}>
        <form onSubmit={submitGroup} className="space-y-4">
          <Field label="Action">
            {p => (
              <Select {...p} value={groupMode} onChange={e => setGroupMode(e.target.value as typeof groupMode)}>
                <option value="add_to_group">Add to group</option>
                <option value="remove_from_group">Remove from group</option>
              </Select>
            )}
          </Field>
          <Field label="Group" required>
            {p => (
              <Select {...p} required value={groupId} onChange={e => setGroupId(e.target.value)}>
                <option value="">Choose a group…</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" loading={mutation.isPending} disabled={!groupId}>Apply</Button>
          </div>
        </form>
      </Modal>

      <Modal open={dialog === 'interval'} onClose={() => setDialog(null)} title={`Check interval for ${n} service${n === 1 ? '' : 's'}`}>
        <form onSubmit={submitInterval} className="space-y-4">
          <Field label="Check every" hint="Heartbeat services use this as the expected period.">
            {p => (
              <Select {...p} value={interval} onChange={e => setInterval_(e.target.value)}>
                {INTERVALS.map(s => <option key={s} value={s}>{s < 60 ? `${s} seconds` : s < 3600 ? `${s / 60} minutes` : `${s / 3600} hour${s === 3600 ? '' : 's'}`}</option>)}
              </Select>
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button type="submit" loading={mutation.isPending}>Apply</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={dialog === 'delete'}
        title={`Delete ${n} service${n === 1 ? '' : 's'}`}
        message={`Their check history and incidents go with them. This cannot be undone.`}
        confirmLabel={`Delete ${n}`}
        requireText={n >= 5 ? 'delete' : undefined}
        danger
        loading={mutation.isPending}
        onConfirm={() => run('delete')}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

const VERB: Record<BulkAction, string> = {
  pause: 'Paused', resume: 'Resumed', delete: 'Deleted', add_tags: 'Tagged', remove_tags: 'Untagged',
  add_to_group: 'Grouped', remove_from_group: 'Ungrouped', set_interval: 'Rescheduled', check: 'Checking',
};

function BarButton({ children, onClick, disabled, danger, haspopup, className = '' }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean; haspopup?: boolean; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-haspopup={haspopup ? 'dialog' : undefined}
      className={`inline-flex items-center justify-center px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        danger
          ? 'text-red-300 hover:bg-red-500/20 dark:text-red-700 dark:hover:bg-red-500/10'
          : 'hover:bg-white/10 dark:hover:bg-black/10'
      } ${className}`}
    >
      {children}
    </button>
  );
}

/** A full-width row in the overflow sheet; 48px so it takes a thumb. */
function SheetAction({ children, onClick, disabled, danger }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`w-full flex items-center px-4 min-h-[3rem] text-sm font-medium text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
          danger
            ? 'text-status-down hover:bg-status-down/10'
            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        {children}
      </button>
    </li>
  );
}
